import React, { useState } from 'react';
import { AlertTriangle, Check, Plus, RefreshCw, Search, Trash2, UserPlus, X } from 'lucide-react';

import { DeliveryDatePicker } from '@/components/ui/delivery-date-picker';
import StakeholderInput from '@/components/StakeholderInput';
import FieldError from '@/components/ui/field-error';
import { formatIST } from '@/lib/utils';
import {
  Card, PhonePage, PrimaryButton, Spinner, TabRow, TopBar,
} from '@/components/phone/primitives';
import { Field, INPUT_CLASS, inputStyle, SelectField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';
import ProductNameCell from './ProductNameCell';
import { StockLine } from './ItemsGrid';
import { RATE_FIELD, orderTotals } from './itemPricing';

/**
 * Booking an order, on the admin's phone.
 *
 * The desktop screen is a spreadsheet: nine columns of items beside a strip of
 * the order's facts, all of it typed in place. A spreadsheet is 58rem wide, and
 * at a handset's width it is a sideways scroll with the amount off the edge of
 * the screen from the item it belongs to.
 *
 * So the same order, one column: the buyer, then one card per item carrying
 * everything the row carried, then the order's facts and its money, in the
 * supervisor app's controls. The state lives on the page; this only draws it,
 * so the phone books exactly what the desktop books.
 *
 * Editing a booked order is the same form filled in, with the same holds the
 * desktop grid keeps: `lockOf(item)` says what a row on the floor will not give
 * up, and the show* flags drop the money, the advance and the stakeholders for
 * the accounts and the moments that do not take them.
 */

const JOB_TYPES = [
  { key: 'new', label: 'New order', testID: 'job-type-new' },
  { key: 'repeat', label: 'Repeat order', testID: 'job-type-repeat' },
];

const RATE_LABELS = [
  ['per_piece', 'Per piece'],
  ['total', 'Lump sum'],
  ['per_sheet', 'Per sheet'],
];

const PRIORITIES = [['low', 'Low'], ['medium', 'Medium'], ['high', 'High']];

const SOURCES = [
  { value: 'phone_call', label: 'Phone call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'walk_in', label: 'Walk-in' },
];

const NEW_CUSTOMER_FIELDS = [
  ['firm_name', 'Firm', 'Company / business'],
  ['customer_name', 'Person name *', 'Contact person'],
  ['mobile_number', 'Mobile *', '10-digit number'],
  ['email', 'Email', 'Optional'],
];

const money = (n) => `₹${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function TextInput({ className = '', ...props }) {
  return <input className={`${INPUT_CLASS} ${className}`} style={inputStyle} {...props} />;
}

/** A few choices side by side, each the size of a thumb. */
function Segmented({ options, value, onChange, label, testIDPrefix }) {
  return (
    <div className="flex gap-2" role="group" aria-label={label}>
      {options.map(([key, text]) => {
        const on = value === key;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={on}
            data-testid={testIDPrefix ? `${testIDPrefix}-${key}` : undefined}
            onClick={() => onChange(key)}
            className="h-11 flex-1 rounded-[10px] border text-[13px]"
            style={{
              backgroundColor: on ? Colors.accent.DEFAULT : Colors.background.primary,
              borderColor: on ? Colors.accent.DEFAULT : Colors.border.default,
              color: on ? Colors.text.inverse : Colors.text.secondary,
              fontWeight: on ? 600 : 500,
            }}
          >
            {text}
          </button>
        );
      })}
    </div>
  );
}

function ItemCard({
  item, idx, customerId, calcItemTotal, onChange, onRemove, autoFocus,
  lock = {}, showMoney = true, stockPlanning = true,
}) {
  const n = idx + 1;
  const set = (patch) => onChange(item.id, patch);
  const priceType = item.price_type || 'per_piece';
  const rateField = RATE_FIELD[priceType];
  const amount = calcItemTotal(item);
  const rateLabel = priceType === 'per_sheet' ? 'Rate per sheet'
    : priceType === 'total' ? 'Lump sum' : 'Rate per piece';

  return (
    <Card className="mt-3" testID={`item-${idx}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.5px]" style={{ color: Colors.text.secondary }}>
          Item {n}
          {lock.quantity && <span className="ml-1.5 normal-case tracking-normal" style={{ color: Colors.semantic.caution }}>· on the floor</span>}
        </span>
        <div className="flex items-center gap-2">
          {showMoney && (
            <span className="text-[14px] font-bold tabular-nums" style={{ color: Colors.text.primary }}>
              {amount ? money(amount) : '—'}
            </span>
          )}
          {!lock.remove && (
            <button
              type="button"
              onClick={() => onRemove(item.id)}
              aria-label={`Remove item ${n}`}
              className="flex h-9 w-9 items-center justify-center rounded-md border"
              style={{ borderColor: Colors.border.default, color: Colors.status.error }}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <Field label="Product">
        <ProductNameCell
          value={item.item_name || ''}
          customerId={customerId}
          onPick={set}
          placeholder="Product name…"
          testId={`item-name-${idx}`}
          className={INPUT_CLASS}
          autoFocus={autoFocus}
        />
        {stockPlanning && <StockLine item={item} idx={idx} set={set} />}
      </Field>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <Field label="Quantity">
          <TextInput
            type="number" inputMode="numeric" value={item.quantity ?? ''}
            onChange={(e) => set({ quantity: e.target.value })}
            disabled={lock.quantity}
            title={lock.quantity ? 'On the floor already — the quantity is what the batch is running to' : undefined}
            aria-label={`Item ${n} quantity`}
            className="text-right tabular-nums disabled:opacity-60"
          />
        </Field>
        {showMoney && (
          <Field label={rateLabel}>
            <div className="flex gap-2">
              {priceType === 'per_sheet' && (
                <TextInput
                  type="number" inputMode="numeric" value={item.pieces_per_sheet ?? ''}
                  onChange={(e) => set({ pieces_per_sheet: e.target.value })}
                  placeholder="ups" aria-label={`Item ${n} pieces per sheet`}
                  className="w-16 px-2 text-right tabular-nums"
                />
              )}
              <TextInput
                type="number" inputMode="decimal" value={item[rateField] ?? ''}
                onChange={(e) => set({ [rateField]: e.target.value })}
                aria-label={`Item ${n} rate`}
                className="text-right tabular-nums"
              />
            </div>
          </Field>
        )}
      </div>

      {showMoney && (
        <div className="mt-3">
          <Segmented
            options={RATE_LABELS}
            value={priceType}
            onChange={(v) => set({ price_type: v })}
            label={`Item ${n} rate unit`}
          />
        </div>
      )}

      <Field label="Delivery date" className="mt-3">
        <TextInput
          type="date" value={item.expected_delivery_date || ''}
          onChange={(e) => set({ expected_delivery_date: e.target.value })}
          aria-label={`Item ${n} delivery date`}
        />
      </Field>

      <Field label="Remarks" className="mt-3">
        <TextInput
          type="text" value={item.special_instructions || ''}
          onChange={(e) => set({ special_instructions: e.target.value })}
          placeholder="Anything the customer said"
          aria-label={`Item ${n} remarks`}
        />
      </Field>
    </Card>
  );
}

export default function PhoneOrderForm({
  jobType, setJobType,
  customerSearch, setCustomerSearch, customers,
  selectedCustomer, setSelectedCustomer,
  showNewCustomer, setShowNewCustomer, newCustomer, setNewCustomer, newCustomerErrors, createCustomerInline,
  deliveryAddress, setDeliveryAddress,
  lineItems, draftId, calcItemTotal, updateItem, addDraft, removeItem,
  orderDetails, setOrderDetails, expectedDeliveryDate, setExpectedDeliveryDate,
  stakeholderTags, setStakeholderTags,
  pastOrders = [], pastItems = [], repeatTab, setRepeatTab, cloneOrder, cloneItem,
  itemsTotal, namedCount, loading, onSubmit, onBack,
  eyebrow = 'NEW ORDER', title = 'Create Order', entryDate,
  testID = 'create-order-page', submitTestID = 'create-order-btn', bottomSubmitTestID = 'create-order-bottom',
  submitLabel = 'Create', bottomLabel = 'Create order',
  lockJobType = false, lockOf, startedCount = 0, splitCount = 0,
  showMoney = true, showAdvance = true, showStakeholders = true, showNarration = true, stockPlanning = true,
}) {
  // the card just added gets the keyboard, so Add item is one tap and typing
  const [focusId, setFocusId] = useState(null);
  const set = (patch) => setOrderDetails((d) => ({ ...d, ...patch }));
  const { discount, gst, total, advance } = orderTotals(itemsTotal, orderDetails);
  const canCreate = !loading && namedCount > 0 && !!selectedCustomer;

  const addItem = () => {
    setFocusId(draftId);
    addDraft({});
  };

  const createButton = (
    <button
      type="button"
      data-testid={submitTestID}
      onClick={onSubmit}
      disabled={!canCreate}
      className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold disabled:opacity-60"
      style={{ backgroundColor: Colors.semantic.positive, color: Colors.text.inverse }}
    >
      {loading
        ? <Spinner size="sm" className="!border-white !border-b-transparent" />
        : <><Check className="h-4 w-4" /> {submitLabel}</>}
    </button>
  );

  return (
    <PhonePage testID={testID}>
      <TopBar
        eyebrow={eyebrow}
        title={title}
        subtitle={selectedCustomer ? selectedCustomer.customer_name : 'Pick a customer to begin'}
        onBack={onBack}
        actions={createButton}
      />

      <div className="flex-1 px-4 pb-10">
        {startedCount > 0 && (
          <div
            className="mt-3 flex items-start gap-2 rounded-[10px] border px-3 py-2.5 text-[12px] leading-[18px]"
            style={{ backgroundColor: Colors.semantic.cautionWash, borderColor: '#E8DCC4', color: Colors.semantic.caution }}
            data-testid="started-notice"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {startedCount} item{startedCount !== 1 ? 's are' : ' is'} already on the floor. Names, rates
              and remarks still change; the quantity is what the batch is running to, and the buyer can no
              longer be corrected.
            </span>
          </div>
        )}
        {splitCount > 0 && (
          <p className="mt-3 px-1 text-[12px]" style={{ color: Colors.text.secondary }}>
            {splitCount} row{splitCount !== 1 ? 's' : ''} from a split job {splitCount !== 1 ? 'are' : 'is'} not
            listed here — a split is worked out on the job card, and its quantities and values stay with it.
          </p>
        )}

        <Card className="mt-3" title="Buyer" aside={lockJobType ? (jobType === 'repeat' ? 'Repeat order' : 'New order') : undefined}>
          {!lockJobType && <TabRow tabs={JOB_TYPES} active={jobType} onSelect={setJobType} />}

          <Field label="Customer *" className="mt-3">
            {selectedCustomer ? (
              <div
                className="flex items-center gap-3 rounded-[10px] border px-3 py-2.5"
                style={{ backgroundColor: Colors.accent.wash, borderColor: '#CFDEE8' }}
                data-testid="selected-customer"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-semibold" style={{ color: Colors.text.primary }}>
                    {selectedCustomer.customer_name}
                  </p>
                  <p className="truncate text-[12px]" style={{ color: Colors.text.secondary }}>
                    {[selectedCustomer.firm_name, selectedCustomer.mobile_number].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }}
                  aria-label="Change customer"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                  style={{ color: Colors.text.secondary }}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <div
                  className="flex h-12 items-center gap-2 rounded-[10px] border px-3"
                  style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
                >
                  <Search className="h-4 w-4 shrink-0" style={{ color: Colors.text.tertiary }} />
                  <input
                    data-testid="customer-search-input"
                    className="min-w-0 flex-1 bg-transparent text-[14px] outline-none"
                    style={{ color: Colors.text.primary }}
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Name, firm or phone number"
                    type="search"
                    autoCapitalize="none"
                    autoCorrect="off"
                  />
                </div>

                {customerSearch.trim().length > 0 && (
                  <ul
                    className="mt-2 overflow-hidden rounded-[10px] border"
                    style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
                  >
                    {customers.length === 0 ? (
                      <li className="px-3 py-3 text-[13px]" style={{ color: Colors.text.tertiary }}>
                        No customer matches that. Add one below.
                      </li>
                    ) : customers.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          data-testid={`customer-${c.id}`}
                          onClick={() => setSelectedCustomer(c)}
                          className="flex w-full items-center justify-between gap-2 border-b px-3 py-3 text-left last:border-b-0"
                          style={{ borderColor: Colors.border.subtle }}
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-[14px] font-medium" style={{ color: Colors.text.primary }}>
                              {c.customer_name}
                            </span>
                            <span className="block truncate text-[12px]" style={{ color: Colors.text.secondary }}>
                              {c.firm_name || '—'} · {c.mobile_number}
                            </span>
                          </span>
                          <span className="shrink-0 text-[11px]" style={{ color: Colors.text.tertiary }}>
                            {c.total_orders || 0} orders
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {!showNewCustomer && (
                  <button
                    type="button"
                    data-testid="add-new-customer-btn"
                    onClick={() => setShowNewCustomer(true)}
                    className="mt-2 flex h-11 w-full items-center justify-center gap-1.5 rounded-[10px] border text-[13px] font-semibold"
                    style={{ borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
                  >
                    <UserPlus className="h-4 w-4" /> New customer
                  </button>
                )}
              </>
            )}
          </Field>

          {showNewCustomer && !selectedCustomer && (
            <div
              className="mt-3 rounded-[10px] border p-3"
              style={{ backgroundColor: Colors.background.secondary, borderColor: Colors.border.default }}
            >
              <p className="mb-3 text-[13px] font-bold" style={{ color: Colors.text.primary }}>New Customer</p>
              <div className="space-y-3">
                {NEW_CUSTOMER_FIELDS.map(([key, label, placeholder]) => (
                  <Field key={key} label={label}>
                    <TextInput
                      value={newCustomer[key] || ''}
                      onChange={(e) => setNewCustomer((p) => ({ ...p, [key]: e.target.value }))}
                      placeholder={placeholder}
                      inputMode={key === 'mobile_number' ? 'tel' : undefined}
                    />
                    <FieldError error={newCustomerErrors?.[key]} />
                  </Field>
                ))}
                <Field label="Address">
                  <TextInput
                    value={newCustomer.address || ''}
                    onChange={(e) => setNewCustomer((p) => ({
                      ...p, address: e.target.value, delivery_address: e.target.value,
                    }))}
                    placeholder="Full address for delivery"
                  />
                </Field>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={createCustomerInline}
                  className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-[10px] text-[13px] font-semibold"
                  style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
                >
                  <Check className="h-4 w-4" /> Save & use
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewCustomer(false)}
                  className="h-11 flex-1 rounded-[10px] border text-[13px] font-medium"
                  style={{
                    backgroundColor: Colors.background.primary,
                    borderColor: Colors.border.default,
                    color: Colors.text.secondary,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <Field label="Delivery address" className="mt-3">
            <TextInput
              data-testid="delivery-address-input"
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              placeholder={selectedCustomer?.delivery_address || selectedCustomer?.address || 'Where it is going'}
            />
          </Field>
        </Card>

        {/* one card per item — what the row carried, now readable at a thumb */}
        <div className="mt-5 flex items-baseline justify-between px-1">
          <h2 className="text-[15px] font-bold" style={{ color: Colors.text.primary }}>
            Items
            {lineItems.length > 0 && (
              <span className="ml-1 text-[13px] font-medium" style={{ color: Colors.text.tertiary }}>
                ({lineItems.length})
              </span>
            )}
          </h2>
          {itemsTotal > 0 && (
            <span className="text-[13px] font-semibold tabular-nums" style={{ color: Colors.text.secondary }}>
              {money(itemsTotal)}
            </span>
          )}
        </div>

        {lineItems.length === 0 && (
          <p className="mt-3 px-1 text-[13px]" style={{ color: Colors.text.tertiary }}>
            No items yet. Name, count and price go here — how it is made is decided on the job card.
          </p>
        )}

        {lineItems.map((item, idx) => (
          <ItemCard
            key={item.id}
            item={item}
            idx={idx}
            customerId={selectedCustomer?.id}
            calcItemTotal={calcItemTotal}
            onChange={updateItem}
            onRemove={removeItem}
            autoFocus={item.id === focusId}
            lock={lockOf ? (lockOf(item) || {}) : {}}
            showMoney={showMoney}
            stockPlanning={stockPlanning}
          />
        ))}

        <button
          type="button"
          data-testid="add-item-btn"
          onClick={addItem}
          className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-[10px] border border-dashed text-[14px] font-semibold"
          style={{ backgroundColor: Colors.accent.wash, borderColor: Colors.accent.DEFAULT, color: Colors.accent.DEFAULT }}
        >
          <Plus className="h-4 w-4" /> Add item
        </button>

        {!lockJobType && jobType === 'repeat' && selectedCustomer && (
          <Card className="mt-3" title="Past orders">
            <TabRow
              tabs={[
                { key: 'orders', label: 'By order', testID: 'repeat-tab-orders' },
                { key: 'items', label: 'By item', testID: 'repeat-tab-items' },
              ]}
              active={repeatTab}
              onSelect={setRepeatTab}
            />
            <div className="mt-2">
              {repeatTab === 'orders' && (pastOrders.length === 0 ? (
                <p className="py-4 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>No past orders found</p>
              ) : pastOrders.slice(0, 5).map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between gap-2 border-b py-3 last:border-b-0"
                  style={{ borderColor: Colors.border.subtle }}
                >
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-semibold" style={{ color: Colors.text.primary }}>{order.order_id}</p>
                    <p className="text-[12px]" style={{ color: Colors.text.secondary }}>
                      {order.line_items?.length || 0} items · {formatIST(order.created_at, { dateOnly: true })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => cloneOrder(order)}
                    className="flex h-9 shrink-0 items-center gap-1 rounded-[10px] border px-3 text-[12px] font-semibold"
                    style={{ borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> Repeat
                  </button>
                </div>
              )))}
              {repeatTab === 'items' && (pastItems.length === 0 ? (
                <p className="py-4 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>No past items found</p>
              ) : pastItems.slice(0, 10).map((item) => (
                <div
                  key={item.source_line_item_id}
                  className="flex items-center justify-between gap-2 border-b py-3 last:border-b-0"
                  style={{ borderColor: Colors.border.subtle }}
                >
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-medium" style={{ color: Colors.text.primary }}>
                      {item.item_name || item.product_subtype}
                    </p>
                    <p className="text-[12px]" style={{ color: Colors.text.secondary }}>
                      {item.order_type} · Qty: {item.quantity}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => cloneItem(item)}
                    aria-label={`Add ${item.item_name || item.product_subtype}`}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border"
                    style={{ borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              )))}
            </div>
          </Card>
        )}

        <Card className="mt-3" title="Order" aside={entryDate ? `Booked ${entryDate}` : undefined}>
          <div className="space-y-3">
            <Field label="Delivery date">
              <DeliveryDatePicker
                value={expectedDeliveryDate}
                onChange={setExpectedDeliveryDate}
                className="h-12 rounded-[10px] text-[14px]"
              />
            </Field>
            <Field label="Priority">
              <Segmented
                options={PRIORITIES}
                value={orderDetails.priority}
                onChange={(v) => set({ priority: v })}
                label="Priority"
                testIDPrefix="priority"
              />
            </Field>
            <Field label="Source">
              <SelectField
                name="order_source"
                label="How the order came in"
                value={orderDetails.order_source}
                options={SOURCES}
                onChange={(v) => set({ order_source: v })}
                placeholder="How it came in"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="PO / Ref no.">
                <TextInput
                  data-testid="po-ref-input"
                  value={orderDetails.po_ref_number}
                  onChange={(e) => set({ po_ref_number: e.target.value })}
                  placeholder="Customer's PO"
                />
              </Field>
              <Field label="PO date">
                <TextInput
                  data-testid="po-date-input"
                  type="date"
                  value={orderDetails.po_date}
                  onChange={(e) => set({ po_date: e.target.value })}
                />
              </Field>
            </div>
            {showStakeholders && <StakeholderInput value={stakeholderTags} onChange={setStakeholderTags} />}
          </div>
        </Card>

        {showMoney && (
        <Card className="mt-3" title="Amount">
          <div className={`grid ${showAdvance ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
            {[
              ['Disc %', 'discount_pct'],
              ['GST %', 'gst_pct'],
              ...(showAdvance ? [['Advance', 'advance_payment']] : []),
            ].map(([label, key]) => (
              <Field key={key} label={label}>
                <TextInput
                  type="number" inputMode="decimal" value={orderDetails[key] ?? ''}
                  onChange={(e) => set({ [key]: e.target.value })}
                  placeholder="0" aria-label={label}
                  className="px-2 text-right tabular-nums"
                />
              </Field>
            ))}
          </div>
          <dl className="mt-3 text-[13px]">
            {[['Items', itemsTotal], ['Discount', -discount], ['GST', gst]].map(([label, value]) => (
              <div key={label} className="flex justify-between border-b py-1.5" style={{ borderColor: Colors.border.subtle }}>
                <dt style={{ color: Colors.text.secondary }}>{label}</dt>
                <dd className="tabular-nums" style={{ color: Colors.text.primary }}>
                  {value < 0 ? '−' : ''}{money(value)}
                </dd>
              </div>
            ))}
            <div className="mt-1 flex justify-between border-t-2 pt-2" style={{ borderColor: Colors.accent.DEFAULT }}>
              <dt className="text-[14px] font-bold" style={{ color: Colors.text.primary }}>Total</dt>
              <dd className="text-[16px] font-bold tabular-nums" style={{ color: Colors.accent.DEFAULT }} data-testid="order-total">
                {money(total)}
              </dd>
            </div>
            {advance > 0 && (
              <div className="flex justify-between py-1.5 text-[12px]">
                <dt style={{ color: Colors.text.secondary }}>Balance after advance</dt>
                <dd className="tabular-nums" style={{ color: Colors.text.primary }}>{money(total - advance)}</dd>
              </div>
            )}
          </dl>
        </Card>
        )}

        {showNarration && (
        <Card className="mt-3" title="Narration">
          <textarea
            value={orderDetails.remarks}
            onChange={(e) => set({ remarks: e.target.value })}
            rows={3}
            aria-label="Narration"
            placeholder="Anything about this order as a whole — terms, packing notes, who to call on delivery."
            className="min-h-[80px] w-full resize-y rounded-[10px] border px-3 py-2.5 text-[14px] outline-none"
            style={inputStyle}
          />
        </Card>
        )}

        <PrimaryButton
          className="mt-5"
          onClick={onSubmit}
          disabled={!canCreate}
          busy={loading}
          testID={bottomSubmitTestID}
        >
          <Check className="h-4 w-4" /> {bottomLabel}
        </PrimaryButton>
        <p className="mt-3 text-center text-[11px]" style={{ color: Colors.text.tertiary }}>
          {namedCount} item{namedCount !== 1 ? 's' : ''}
          {showMoney && itemsTotal > 0 && ` · ${money(itemsTotal)}`}
        </p>
      </div>
    </PhonePage>
  );
}
